import React, { createContext, useContext } from 'react'
import styled from '@emotion/styled'
import { colors, font, radius, shadow, spacing } from '@mobi/component-library/Foundations'

type ListProps = {
  children: React.ReactNode
  title?: string
  subTitle?: string
  subTitleAlign?: 'below' | 'side'
  headingSize?: 'small' | 'large'
  itemSize?: 'small' | 'medium' | 'dynamic'
  variant?: 'full-width' | 'card' | 'card-groups'
  paddingBottom?: React.CSSProperties['paddingBottom']
  shouldUseInlinePadding?: boolean
  bottomBorder?: 'soft' | 'hard' | 'none'
  titleRightAlignedContent?: React.ReactNode
  testId?: string
}

const ListContext = createContext<NonNullable<Pick<ListProps, 'itemSize'>>>({ itemSize: 'small' })

export const List: React.FC<ListProps> = ({
  children,
  title,
  subTitle,
  subTitleAlign = 'below',
  headingSize = 'small',
  itemSize = 'small',
  paddingBottom,
  shouldUseInlinePadding = true,
  bottomBorder = 'soft',
  variant = 'full-width',
  titleRightAlignedContent,
  testId = 'List',
}) => {
  return (
    <ListContext.Provider value={{ itemSize }}>
      <ListContainerStyled
        data-testid={testId}
        data-list-inline-padding={shouldUseInlinePadding}
        data-list-bottom-border={bottomBorder}
        data-list-variant={variant}
        style={{ paddingBottom }}
      >
        {title && (
          <div
            className='list__header'
            data-list-heading-size={headingSize}
            data-list-subtitle-align={subTitleAlign}
          >
            <h3>
              <strong>{title}</strong>
              {subTitle}
            </h3>

            {titleRightAlignedContent}
          </div>
        )}

        <div className='list__items'>
          <ul>{children}</ul>
        </div>
      </ListContainerStyled>
    </ListContext.Provider>
  )
}

export const ListItem = React.forwardRef<
  HTMLLIElement,
  ListItemContent & {
    testId?: string
    onClick?: () => void
    icon?: React.ReactNode
    contentRight?: React.ReactNode
    sizeOverride?: ListProps['itemSize']
  }
>(
  (
    {
      text,
      subText,
      content,
      icon,
      testId,
      contentRight,
      onClick,
      sizeOverride,
      shouldTruncateText,
    },
    ref
  ) => {
    const size = useContext(ListContext).itemSize

    return (
      <li
        ref={ref}
        onClick={onClick}
        data-testid={testId}
        data-list-item-link={!!onClick}
        data-list-item-size={sizeOverride || size}
        data-list-item-truncate-text={shouldTruncateText}
      >
        <div>
          {icon && <div className='list-item__icon'>{icon}</div>}

          <div className='list-item__content'>
            <div>
              {text && (
                <span
                  className={`list-item__content-text ${subText ? 'list-item__content-text--subtext' : ''}`}
                >
                  <span>{text}</span>
                  {subText}
                </span>
              )}

              {content}
            </div>

            {contentRight && <div>{contentRight}</div>}
          </div>
        </div>
      </li>
    )
  }
)
ListItem.displayName = 'ListItem'

export const ListGroupHeader: React.FC<{ title: React.ReactNode; children: React.ReactNode }> = ({
  title,
  children,
}) => {
  return (
    <li className='list-group-header'>
      <section>
        <div>{title}</div>
        <div>{children}</div>
      </section>
    </li>
  )
}

// ======
// Styles
// ======

const ListContainerStyled = styled.div({
  '--listItemSize': '4.8rem',
  '--listItemMinSize': '--listItemSize',
  fontFamily: font.family.primary,

  '> div.list__header': {
    boxSizing: 'border-box',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: spacing.smx1,
    minHeight: '3.2rem',

    '> h3': {
      all: 'unset',
      fontSize: font.size.lg.fontSize,
      letterSpacing: font.size.lg.letterSpacing,
      lineHeight: font.size.lg.lineHeight,
      fontWeight: font.weight.medium,
      color: colors.neutral[800],

      strong: {
        display: 'block',
        fontWeight: font.weight.semibold,
        color: colors.lavender[900],
      },
    },

    '&[data-list-subtitle-align="side"] > h3': {
      strong: { display: 'inline', marginRight: spacing.smx2 },
    },

    '&[data-list-heading-size="large"] > h3': {
      fontSize: font.size.xl.fontSize,
      letterSpacing: font.size.xl.letterSpacing,
      lineHeight: font.size.xl.lineHeight,
      paddingBlock: spacing.smx1,
    },
  },

  '> .list__items': {
    '[data-list-item-size="small"]': {
      '--listItemSize': '4.8rem',
      '--listItemMinSize': '--listItemSize',
    },
    '[data-list-item-size="medium"]': {
      '--listItemSize': '6rem',
      '--listItemMinSize': '--listItemSize',
    },
    '[data-list-item-size="dynamic"]': {
      '--listItemSize': 'auto',
      '--listItemMinSize': '4.8rem',
    },

    '> ul:not(:has(ul))': { background: colors.white },
    '> ul, ul > li': { padding: 0, margin: 0, listStyle: 'none' },

    '> ul > li': {
      display: 'grid',
      gridTemplateRows: '1fr',
      overflow: 'hidden',
      WebkitTapHighlightColor: 'transparent',

      '> div': {
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        gap: spacing.smx1,
        overflow: 'hidden',

        fontFamily: font.family.primary,
        fontSize: font.size.md.fontSize,
        letterSpacing: font.size.md.letterSpacing,
        lineHeight: font.size.md.lineHeight,
        fontWeight: font.weight.regular,
        color: colors.black,
        backgroundColor: colors.white,

        '> div': { display: 'flex', alignItems: 'center' },

        '> .list-item__icon': { flexShrink: 0 },

        '> .list-item__content': {
          boxSizing: 'border-box',
          flex: 1,
          display: 'flex',
          justifyContent: 'space-between',
          overflow: 'hidden',
          height: 'var(--listItemSize)',
          minHeight: 'var(--listItemMinSize)',
          borderBottom: `0.5px solid ${colors.neutral[200]}`,

          color: colors.black,
          fontSize: font.size.lg.fontSize,
          letterSpacing: font.size.lg.letterSpacing,
          lineHeight: font.size.lg.lineHeight,
          fontWeight: font.weight.regular,

          '> div:nth-of-type(1)': {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',

            '.list-item__content-text': {
              display: 'flex',
              flexDirection: 'column',

              '> span': { display: 'block' },

              '&--subtext': {
                color: colors.neutral[800],
                fontSize: font.size.md.fontSize,
                letterSpacing: font.size.md.letterSpacing,
                lineHeight: font.size.md.lineHeight,

                '> span': {
                  fontSize: font.size.lg.fontSize,
                  letterSpacing: font.size.lg.letterSpacing,
                  lineHeight: font.size.lg.lineHeight,
                  fontWeight: font.weight.medium,
                  color: colors.black,
                },
              },
            },
          },

          '> div:nth-of-type(2)': {
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: spacing.smx2,
          },
        },
      },

      '&[data-list-item-link="true"] > div': {
        cursor: 'pointer',
        transition: 'color 0.15s ease, background 0.15s ease',

        '> *': {
          transition: 'opacity 0.15s ease',
          opacity: 1,
        },

        '@supports selector(:has(*))': {
          '@media (hover: hover) and (pointer: fine)': {
            ':hover:not(:has(button:hover))': {
              transition: 'color 0.15s ease, background 0.15s ease',
              backgroundColor: colors.neutral[100],
            },
          },
          ':active:not(:has(button:active))': { backgroundColor: colors.neutral[300] },
          ':active:not(:has(button:active)) > *': { opacity: 0.5 },
        },

        '@supports not selector(:has(*))': {
          '@media (hover: hover) and (pointer: fine)': {
            ':hover': {
              transition: 'color 0.15s ease, background 0.15s ease',
              backgroundColor: colors.neutral[100],
            },
          },
          ':active': { backgroundColor: colors.neutral[300] },
          ':active > *': { opacity: 0.5 },
        },
      },

      '&[data-list-item-truncate-text="true"] .list-item__content-text > span': {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
    },

    '> ul > li.list-group-header': {
      border: 0,
      height: 'unset',
      minHeight: 'auto',
      marginBottom: spacing.smx1,
      backgroundColor: colors.white,

      '&:last-of-type': { marginBottom: 0 },

      '> section': {
        flex: 1,

        '> div:first-of-type': {
          paddingTop: spacing.smx1,
          fontSize: font.size.lg.fontSize,
          letterSpacing: font.size.lg.letterSpacing,
          lineHeight: font.size.lg.lineHeight,
          fontWeight: font.weight.semibold,
          color: colors.lavender[900],

          ':empty': { padding: 0 },
        },
      },
    },
  },

  '&[data-list-inline-padding="true"]': {
    '> div.list__header': { paddingInline: spacing.md },

    '> .list__items > ul > li:not(.list-group-header) > div': {
      paddingLeft: spacing.md,
      '.list-item__content': { paddingRight: spacing.md },
    },

    '.list-group-header > section > div:first-of-type': {
      paddingLeft: spacing.md,
    },
  },

  '&:not([data-list-bottom-border="none"], [data-list-variant="card"], [data-list-variant="card-groups"])':
    {
      '&[data-list-bottom-border="soft"]': {
        '--bottomBorder': `0.5px solid ${colors.neutral[200]}`,
      },
      '&[data-list-bottom-border="hard"]': {
        '--bottomBorder': `0.5px solid ${colors.neutral[400]}`,
      },

      '> .list__items > ul > li:last-of-type': {
        '> div > .list-item__content': { borderBottom: 0 },
        '> div': { borderBottom: 'var(--bottomBorder, 0)' },
      },
    },

  '&[data-list-variant="card"]': {
    '> .list__items': {
      overflow: 'hidden',
      border: '1px solid ' + colors.neutral[300],
      borderRadius: radius.lgx3,
      boxShadow: shadow.xs,
    },

    '> .list__items > ul > li:last-of-type': {
      '> div > .list-item__content': { borderBottom: 0 },
    },
  },

  '&[data-list-variant="card-groups"]': {
    'ul > li.list-group-header': {
      overflow: 'hidden',
      border: '1px solid ' + colors.neutral[300],
      borderRadius: radius.lgx3,
      boxShadow: shadow.xs,
    },

    '.list__items > ul > li:last-of-type': {
      '> div > .list-item__content': { borderBottom: 0 },
      '> div': { borderBottom: 0 },
    },
  },
})

// =====
// Types
// =====

type ListItemContent =
  | {
      text: string
      subText?: string
      shouldTruncateText?: boolean
      content?: never
    }
  | {
      content: React.ReactNode
      text?: never
      subText?: never
      shouldTruncateText?: never
    }
